/**
 * Documents tab — v2.5 UX overhaul.
 *
 * Compact upload bar + filter text input + type pills + flat list.
 * Row click opens the editor; secondary actions live in an overflow
 * menu per row.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DropzoneArea from "../components/DropzoneArea.jsx";
import DocumentRow from "../components/DocumentRow.jsx";
import EmptyState from "../components/EmptyState.jsx";
import ErrorBubble from "../components/ErrorBubble.jsx";
import { AGENT_NAMES } from "../constants/agents.js";
import { downloadFile } from "../services/documents.js";
import { useStore } from "../store/useStore.js";

const RESEARCH_PREFIX = "agent-outputs/research/";
const AGENT_PREFIX = "agent-outputs/";

function classify(doc) {
  if (doc.filename.startsWith(RESEARCH_PREFIX)) return "research";
  if (doc.filename.startsWith(AGENT_PREFIX)) return "agent";
  return "upload";
}

function parseDisplayName(doc) {
  if (doc.filename.startsWith(RESEARCH_PREFIX)) {
    const rest = doc.filename.slice(RESEARCH_PREFIX.length);
    const match = rest.match(/^(\d+)_(.+)\.md$/);
    if (match) {
      const agentId = Number(match[1]);
      return AGENT_NAMES[agentId] || match[2].replace(/_/g, " ");
    }
    return rest;
  }
  return doc.filename.split("/").pop();
}

function parseAgentId(doc) {
  if (!doc.filename.startsWith(RESEARCH_PREFIX)) return null;
  const match = doc.filename.slice(RESEARCH_PREFIX.length).match(/^(\d+)_/);
  return match ? Number(match[1]) : null;
}

export default function ProjectDocumentsTab() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const documents = useStore((s) => s.documents);
  const loading = useStore((s) => s.documentsLoading);
  const error = useStore((s) => s.documentsError);
  const loadDocuments = useStore((s) => s.loadDocuments);
  const removeDocument = useStore((s) => s.removeDocument);
  const regenerateAgent = useStore((s) => s.regenerateAgent);
  const regeneratingAgents = useStore((s) => s.regeneratingAgents);
  const pushToast = useStore((s) => s.pushToast);

  const [deleteError, setDeleteError] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [activeType, setActiveType] = useState("all");

  useEffect(() => {
    loadDocuments(projectId);
  }, [loadDocuments, projectId]);

  const { enriched, counts } = useMemo(() => {
    const enriched = documents.map((doc) => ({
      ...doc,
      _type: classify(doc),
      _displayName: parseDisplayName(doc),
      _agentId: parseAgentId(doc),
    }));
    const counts = { research: 0, upload: 0, agent: 0 };
    for (const d of enriched) counts[d._type]++;
    return { enriched, counts };
  }, [documents]);

  const query = filterText.trim().toLowerCase();
  const filtered = enriched
    .filter((d) => activeType === "all" || d._type === activeType)
    .filter(
      (d) =>
        !query ||
        d._displayName.toLowerCase().includes(query) ||
        d.filename.toLowerCase().includes(query),
    )
    .sort(
      (a, b) =>
        new Date(b.lastModified || 0) - new Date(a.lastModified || 0),
    );

  const pills = [
    { id: "all", label: "All", count: enriched.length },
    { id: "research", label: "Research", count: counts.research },
    { id: "upload", label: "Uploads", count: counts.upload },
    { id: "agent", label: "Agent", count: counts.agent },
  ];

  const handleDelete = async (filename) => {
    setDeleteError(null);
    try {
      await removeDocument(projectId, filename);
      pushToast(`${filename} deleted`, "info");
    } catch (err) {
      setDeleteError(err.message);
    }
  };

  const handleOpen = (filename) => {
    navigate(
      `/projects/${projectId}/editor?filename=${encodeURIComponent(filename)}`,
    );
  };

  const handleDownload = async (filename) => {
    try {
      await downloadFile(projectId, filename);
    } catch (err) {
      pushToast(`Download failed: ${err.message}`, "error");
    }
  };

  const handleRegenerate = async (doc) => {
    if (typeof doc._agentId !== "number") return;
    if (doc._agentId === 0) {
      pushToast(
        "Shared research can't be regenerated individually — run a full pipeline.",
        "info",
      );
      return;
    }
    try {
      await regenerateAgent(projectId, doc._agentId);
      pushToast(`${doc._displayName} regenerating…`, "info");
    } catch (err) {
      pushToast(`Regenerate failed: ${err.message}`, "error");
    }
  };

  return (
    <div className="project-documents">
      <DropzoneArea projectId={projectId} compact />

      <div className="documents-toolbar">
        <input
          type="search"
          className="documents-filter"
          placeholder="Filter documents..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          aria-label="Filter documents"
        />
        <div className="documents-pills" role="tablist">
          {pills.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={activeType === p.id}
              className={`type-pill${activeType === p.id ? " type-pill--active" : ""}`}
              onClick={() => setActiveType(p.id)}
            >
              {p.label} {p.count}
            </button>
          ))}
        </div>
      </div>

      {(error || deleteError) && (
        <ErrorBubble message={error || deleteError} />
      )}

      {loading && documents.length === 0 ? (
        <div className="conversation-list-loading">Loading documents…</div>
      ) : documents.length === 0 ? (
        <EmptyState
          title="No documents yet"
          description="Upload a document to ground your coaching conversations in your own research."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No matches"
          description={`No documents match the current filter.`}
        />
      ) : (
        <ul className="documents-list">
          {filtered.map((doc) => (
            <DocumentRow
              key={doc.filename}
              doc={doc}
              type={doc._type}
              displayTitle={doc._displayName}
              onOpen={handleOpen}
              onDownload={handleDownload}
              onDelete={handleDelete}
              onRegenerate={
                doc._type === "research" && doc._agentId !== 0
                  ? handleRegenerate
                  : undefined
              }
              regenerating={
                typeof doc._agentId === "number" &&
                regeneratingAgents.includes(doc._agentId)
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}
